import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FiExternalLink, FiCopy, FiRefreshCw, FiX, FiEye, FiCode, FiCheck, FiDownload } from 'react-icons/fi';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import JSZip from 'jszip';
import { getApiBaseUrl, fetchPreviewUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';
import './WebsitePreviewPanel.css';

const BUILDING_PHRASES = [
  'Applying changes...',
  'Coding...',
  'Writing HTML...',
  'Brewing CSS...',
  'Wiring up JavaScript...',
  'Pushing pixels...',
  'Compiling creativity...',
  'Crafting the UI...',
  'Painting the canvas...',
  'Almost there...',
];

const MATRIX_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEF<>/{}();:=';

const MatrixRain = React.memo(() => {
  const columns = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      chars: Array.from({ length: 18 }, () =>
        MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)]
      ).join('\n'),
      delay: Math.random() * -20,
      duration: 4 + Math.random() * 6,
      left: i * 7.7,
      opacity: 0.15 + Math.random() * 0.55,
    })),
  []);

  return (
    <div className="matrix-rain">
      {columns.map(col => (
        <div
          key={col.id}
          className="matrix-column"
          style={{
            left: `${col.left}%`,
            animationDuration: `${col.duration}s`,
            animationDelay: `${col.delay}s`,
            opacity: col.opacity,
          }}
        >
          {col.chars}
        </div>
      ))}
    </div>
  );
});

const BuildingOverlay = () => {
  const [phraseIndex, setPhraseIndex] = useState(() =>
    Math.floor(Math.random() * BUILDING_PHRASES.length)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % BUILDING_PHRASES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="preview-building-overlay">
      <MatrixRain />
      <div className="pixel-code-lines">
        <div className="pixel-line line-1" />
        <div className="pixel-line line-2" />
        <div className="pixel-line line-3" />
        <div className="pixel-line line-4" />
        <div className="pixel-line line-5" />
      </div>
      <span className="preview-building-text" key={phraseIndex}>
        {BUILDING_PHRASES[phraseIndex]}
      </span>
    </div>
  );
};

const EXT_TO_LANGUAGE = {
  '.html': 'html',
  '.css': 'css',
  '.js': 'javascript',
  '.json': 'json',
  '.md': 'markdown',
  '.csv': 'plaintext',
  '.txt': 'plaintext',
  '.svg': 'xml',
};

function getLanguage(filename) {
  const ext = '.' + filename.split('.').pop().toLowerCase();
  return EXT_TO_LANGUAGE[ext] || 'plaintext';
}

function isTextFile(filename) {
  const ext = '.' + filename.split('.').pop().toLowerCase();
  return ext in EXT_TO_LANGUAGE;
}

const WebsitePreviewPanel = ({ websitePreview, onClose, onRefresh, isBuilding }) => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('preview');
  const [fileContent, setFileContent] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  // Signed iframe URL minted on demand. The iframe never loads from the
  // SSO-gated /api/websites/... URL directly, because Firefox / Edge will
  // refuse to attach the kai_session cookie to sub-resource requests from
  // a sandboxed iframe — see backend/routers/website_routes.py for the
  // full rationale.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const toastTimer = useRef(null);
  const prevRefreshKey = useRef(null);

  const { project_id, files, refreshKey } = websitePreview || {};

  // Show toast when refreshKey changes (preview was updated), but not on first mount
  useEffect(() => {
    if (prevRefreshKey.current !== null && refreshKey !== prevRefreshKey.current) {
      setShowToast(true);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setShowToast(false), 3000);
    }
    prevRefreshKey.current = refreshKey;
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, [refreshKey]);

  const fetchFileContent = useCallback(async (filename) => {
    setFileLoading(true);
    setFileError(null);
    setFileContent(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/websites/${project_id}/${filename}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load ${filename}`);
      const text = await res.text();
      setFileContent(text);
    } catch (err) {
      setFileError(err.message);
    } finally {
      setFileLoading(false);
    }
  }, [project_id]);

  useEffect(() => {
    if (activeTab !== 'preview' && project_id) {
      fetchFileContent(activeTab);
    }
  }, [activeTab, project_id, fetchFileContent]);

  useEffect(() => {
    setActiveTab('preview');
    setFileContent(null);
    setFileError(null);
  }, [project_id]);

  useEffect(() => {
    if (activeTab !== 'preview' && project_id) {
      fetchFileContent(activeTab);
    }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mint a fresh signed preview URL whenever the project changes or the
  // user explicitly refreshes the preview. Signatures expire (default 4h);
  // re-minting on every (re)open keeps the iframe load reliable for chats
  // older than the TTL.
  useEffect(() => {
    if (!project_id) {
      setPreviewUrl(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewError(null);
    setPreviewUrl(null);
    fetchPreviewUrl(project_id)
      .then((url) => {
        if (cancelled) return;
        // Append the refreshKey as a cache-buster so updated builds aren't
        // served from the browser's cache for the same signed URL.
        const sep = url.includes('?') ? '&' : '?';
        setPreviewUrl(`${url}${sep}_t=${refreshKey}`);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to mint preview URL:', err);
        setPreviewError(err.message || 'Could not load preview');
      });
    return () => { cancelled = true; };
  }, [project_id, refreshKey]);

  if (!websitePreview) return null;

  const textFiles = (files || []).filter(isTextFile);

  return (
    <div className="preview-panel">
      <div className="preview-panel-header">
        <span className="preview-panel-title">Preview</span>
        <div className="preview-panel-actions">
          <button
            className="preview-panel-btn"
            onClick={() => { onRefresh(); setActiveTab('preview'); }}
            title="Refresh preview"
          >
            <FiRefreshCw size={14} />
          </button>
          <button
            className="preview-panel-btn"
            onClick={() => window.open(
              `${getApiBaseUrl()}/websites/${project_id}/index.html`, '_blank'
            )}
            title="Open in new tab"
          >
            <FiExternalLink size={14} />
          </button>
          {project_id && (
            <button
              className="preview-panel-btn"
              onClick={() => navigator.clipboard.writeText(
                `${getApiBaseUrl()}/websites/${project_id}/index.html`
              )}
              title="Copy link"
            >
              <FiCopy size={14} />
            </button>
          )}
          {files && files.length > 0 && (
            <button
              className="preview-panel-btn"
              onClick={async () => {
                const zip = new JSZip();
                await Promise.all(files.map(async (f) => {
                  try {
                    const res = await fetch(`${getApiBaseUrl()}/websites/${project_id}/${f}`, {
                      credentials: 'include',
                    });
                    if (!res.ok) return;
                    const blob = await res.blob();
                    zip.file(f, blob);
                  } catch { /* skip failed files */ }
                }));
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const objectUrl = URL.createObjectURL(zipBlob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = `website-${(project_id || 'project').slice(0, 8)}.zip`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                  URL.revokeObjectURL(objectUrl);
                }, 0);
              }}
              title="Download all files as ZIP"
            >
              <FiDownload size={14} />
            </button>
          )}
          <button
            className="preview-panel-btn preview-panel-close"
            onClick={onClose}
            title="Close preview"
          >
            <FiX size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="preview-panel-tabs">
        <button
          className={`preview-panel-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          <FiEye size={12} />
          <span>Preview</span>
        </button>
        {textFiles.map((f) => (
          <button
            key={f}
            className={`preview-panel-tab ${activeTab === f ? 'active' : ''}`}
            onClick={() => setActiveTab(f)}
          >
            <FiCode size={12} />
            <span>{f}</span>
          </button>
        ))}
      </div>

      {/* Content area (relative for overlay positioning) */}
      <div className="preview-panel-content">
        {/* Building overlay */}
        {isBuilding && <BuildingOverlay />}

        {/* Updated toast */}
        {showToast && !isBuilding && (
          <div className="preview-updated-toast">
            <FiCheck size={14} />
            <span>Preview updated</span>
          </div>
        )}

        {activeTab === 'preview' ? (
          previewError ? (
            <div className="preview-panel-code-status preview-panel-code-error">
              {previewError}
            </div>
          ) : previewUrl ? (
            <iframe
              key={`${refreshKey}-${previewUrl}`}
              src={previewUrl}
              sandbox="allow-scripts"
              className="preview-panel-iframe"
              title="Website Preview"
            />
          ) : (
            <div className="preview-panel-code-status">Loading preview...</div>
          )
        ) : (
          <div className="preview-panel-code">
            {fileLoading && (
              <div className="preview-panel-code-status">Loading...</div>
            )}
            {fileError && (
              <div className="preview-panel-code-status preview-panel-code-error">{fileError}</div>
            )}
            {fileContent !== null && !fileLoading && (
              <SyntaxHighlighter
                language={getLanguage(activeTab)}
                style={isDarkMode ? oneDark : oneLight}
                showLineNumbers
                wrapLongLines
                customStyle={{
                  margin: 0,
                  borderRadius: 0,
                  fontSize: '12px',
                  lineHeight: '1.5',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                {fileContent}
              </SyntaxHighlighter>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsitePreviewPanel;
